import { ZuruflexApiError } from '@zuruflex/api-client';
import { PARTNER_AGREEMENT } from '@zuruflex/core/legal';
import { hasAcceptedAgreement, PARTNER_TERMS_SLUG } from '@zuruflex/types';
import { Alert, Button, Checkbox, ContentDocument, LegalSections, Logo, Skeleton } from '@zuruflex/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

/**
 * Zuruflex's partner agreement, in front of the whole portal until it is signed.
 *
 * A screen rather than a banner, and one that cannot be dismissed, because
 * everything behind it — publishing a car, taking a hire, being paid —
 * happens *under* this agreement. A partner working for a week and then
 * being asked to accept it retroactively is the situation that would have
 * to be explained afterwards.
 *
 * One piece of code covers both the first sign-in and every later
 * republish: the condition is a version comparison, not a boolean, so
 * an owner publishing a new `partner-terms` in the dashboard raises the
 * version in force and puts this back in front of everyone on their next
 * page load.
 *
 * The wording is the published page, with the copy the code ships as the
 * answer whenever the API cannot give one.
 *
 * The clauses themselves are untranslated on purpose. The chrome around
 * them is not — see `docs/I18N.md`.
 */
export function AgreementGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation(['partner', 'common']);
  const { partner, user, refreshPartner, signOut } = useAuth();

  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const outstanding = !!partner && !hasAcceptedAgreement(partner);
  const published = useQuery({
    queryKey: ['content', PARTNER_TERMS_SLUG],
    queryFn: () => api.contentPage(PARTNER_TERMS_SLUG),
    enabled: outstanding,
    retry: false,
    staleTime: 60_000,
  });

  const accept = useMutation({
    // The version shown is the one in force: the server refuses any other.
    mutationFn: () => api.acceptPlatformTerms({ version: partner!.agreementVersion }),
    onSuccess: () => refreshPartner(),
    onError: (cause) =>
      setError(cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected')),
  });

  if (!partner || !outstanding) return <>{children}</>;

  const title = published.data?.title ?? PARTNER_AGREEMENT.title;
  const lead = published.data ? published.data.lead : PARTNER_AGREEMENT.lead;

  /*
   * Only an owner can sign for the business, which is what the API
   * enforces. A manager is told who has to do it rather than shown a
   * button that will come back 403 — and is deliberately not let through
   * in the meantime, because the gate is about the account, not the person.
   */
  const isOwner = user?.role === 'OWNER';
  const returning = (partner.platformTermsVersion ?? 0) > 0;

  return (
    <div className="min-h-screen bg-surface-sunken py-10">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4">
        <header className="flex items-center justify-between gap-4">
          <Logo className="h-7" />
          <Button variant="ghost" size="sm" onClick={() => void signOut()}>
            {t('common:action.signOut')}
          </Button>
        </header>

        <div className="rounded-card border border-border-default bg-surface p-6 sm:p-8">
          <h1 className="font-display text-2xl font-bold tracking-tight">
            {title}
          </h1>
          {lead ? <p className="mt-2 text-sm text-text-muted">{lead}</p> : null}

          {/* A partner who agreed to an earlier version is not a new one,
              and telling them so is what stops this reading as a bug. */}
          <Alert variant={returning ? 'info' : 'brand'} className="mt-6">
            {t(returning ? 'terms.platform.updated' : 'terms.platform.first')}
          </Alert>

          <div className="mt-8 max-h-[50vh] overflow-y-auto rounded-xl border border-border-default p-5">
            {/*
              Waiting is better than showing the copy the code ships for a
              moment and then swapping it: a partner could read — and tick
              for — wording that was already out of date. The code copy is
              only for when the page cannot be fetched at all.
            */}
            {published.isPending ? (
              <div className="space-y-3" aria-busy="true">
                <Skeleton className="h-5 w-1/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ) : published.data ? (
              <ContentDocument doc={published.data.doc} size="sm" />
            ) : (
              <LegalSections sections={PARTNER_AGREEMENT.sections} />
            )}
          </div>

          {error ? (
            <Alert variant="danger" className="mt-6">
              {error}
            </Alert>
          ) : null}

          {isOwner ? (
            <div className="mt-8 flex flex-col gap-4">
              <label className="flex items-start gap-3 text-sm">
                <Checkbox
                  checked={agreed}
                  onCheckedChange={(next) => setAgreed(next === true)}
                  className="mt-0.5"
                />
                <span>{t('terms.platform.agree', { name: partner.displayName })}</span>
              </label>
              <Button
                className="self-start"
                disabled={!agreed || published.isPending}
                isLoading={accept.isPending}
                onClick={() => accept.mutate()}
              >
                {t('terms.platform.accept')}
              </Button>
            </div>
          ) : (
            <Alert variant="warning" className="mt-8">
              {t('terms.platform.ownerOnly')}
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
}
